import type { AcceptJsOpaqueData } from "@/hooks/useAcceptJs";

export type HttpMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
export type QueryValue = string | number | boolean | null | undefined;
export type Query = Record<string, QueryValue | QueryValue[]>;

/** Normalized Problem Details (RFC 9457), including API-specific extension fields. */
export interface ProblemDetails {
    type: string;
    title: string;
    status: number;
    detail?: string;
    instance?: string;
    [extension: string]: unknown;
}

/** status is the actual HTTP status; problem.status may reflect the server's reported status. */
export type ApiResponse<T> =
    | { ok: true; status: number; data: T; problem: null }
    | { ok: false; status: number; data: undefined; problem: ProblemDetails };

export interface RequestOptions {
    /** Query parameters to append to the URL. */
    query?: Query;
    /** Request headers. */
    headers?: Record<string, string>;
    /** Request body. Will be JSON-stringified if not a string or FormData. */
    body?: unknown;
    /** Abort signal to cancel the request. */
    signal?: AbortSignal;
    /** Return HTTP status and problem details instead of throwing for non-2xx responses. */
    returnResponse?: boolean;
}

type GetRequestOptions = Omit<RequestOptions, "body">;
type ResponseOptions = RequestOptions & { returnResponse: true };
type DataOptions = RequestOptions & { returnResponse?: false };

export interface PqClientOptions {
    /** Base URL for the API. Defaults to https://pquserbeta.cd-data.com/api/v1 */
    baseUrl?: string;
    /** Custom fetch implementation (for testing or older runtimes). Defaults to global fetch. */
    fetch?: typeof fetch;
}

export class PqClient {
    private readonly baseUrl: string;
    private readonly fetchImpl: typeof fetch;

    constructor(options: PqClientOptions = {}) {
        this.baseUrl = (options.baseUrl ?? import.meta.env.VITE_PQ_API_URL).replace(/\/+$/, "");
        this.fetchImpl = options.fetch ?? globalThis.fetch.bind(globalThis);
    }

    get<T>(accessToken: string, path: string, options: Omit<ResponseOptions, "body">): Promise<ApiResponse<T>>;
    get<T>(accessToken: string, path: string, options?: Omit<DataOptions, "body">): Promise<T>;
    get<T>(accessToken: string, path: string, options?: GetRequestOptions): Promise<T | ApiResponse<T>>;
    get<T>(accessToken: string, path: string, options?: GetRequestOptions) {
        return this.request<T>(accessToken, "GET", path, options);
    }
    post<T>(accessToken: string, path: string, body: unknown, options: ResponseOptions): Promise<ApiResponse<T>>;
    post<T>(accessToken: string, path: string, body?: unknown, options?: DataOptions): Promise<T>;
    post<T>(accessToken: string, path: string, body?: unknown, options?: RequestOptions): Promise<T | ApiResponse<T>>;
    post<T>(accessToken: string, path: string, body?: unknown, options?: RequestOptions) {
        return this.request<T>(accessToken, "POST", path, { ...options, body });
    }
    put<T>(accessToken: string, path: string, body: unknown, options: ResponseOptions): Promise<ApiResponse<T>>;
    put<T>(accessToken: string, path: string, body?: unknown, options?: DataOptions): Promise<T>;
    put<T>(accessToken: string, path: string, body?: unknown, options?: RequestOptions): Promise<T | ApiResponse<T>>;
    put<T>(accessToken: string, path: string, body?: unknown, options?: RequestOptions) {
        return this.request<T>(accessToken, "PUT", path, { ...options, body });
    }
    patch<T>(accessToken: string, path: string, body: unknown, options: ResponseOptions): Promise<ApiResponse<T>>;
    patch<T>(accessToken: string, path: string, body?: unknown, options?: DataOptions): Promise<T>;
    patch<T>(accessToken: string, path: string, body?: unknown, options?: RequestOptions): Promise<T | ApiResponse<T>>;
    patch<T>(accessToken: string, path: string, body?: unknown, options?: RequestOptions) {
        return this.request<T>(accessToken, "PATCH", path, { ...options, body });
    }
    delete<T = void>(accessToken: string, path: string, options: ResponseOptions): Promise<ApiResponse<T>>;
    delete<T = void>(accessToken: string, path: string, options?: DataOptions): Promise<T>;
    delete<T = void>(accessToken: string, path: string, options?: RequestOptions): Promise<T | ApiResponse<T>>;
    delete<T = void>(accessToken: string, path: string, options?: RequestOptions) {
        return this.request<T>(accessToken, "DELETE", path, options);
    }

    /**
     * Send a request authenticated with the given WorkOS access token.
     * By default, resolves with the parsed body and rejects with ApiError for non-2xx responses.
     * With returnResponse: true, resolves with { ok, status, data, problem } for every HTTP response.
     * Empty responses (including 204/205) have undefined data; use T = void for those endpoints.
     * Network failures and aborted requests reject in either mode.
     *
     * @example
     * const result = await client.get<PqUser>(token, "/mobile/me", { returnResponse: true });
     * if (!result.ok) {
     *     console.error(result.status, result.problem.type, result.problem.detail);
     * }
     */
    request<T>(accessToken: string, method: HttpMethod, path: string, options: ResponseOptions): Promise<ApiResponse<T>>;
    request<T>(accessToken: string, method: HttpMethod, path: string, options?: DataOptions): Promise<T>;
    request<T>(accessToken: string, method: HttpMethod, path: string, options?: RequestOptions): Promise<T | ApiResponse<T>>;
    async request<T>(
        accessToken: string,
        method: HttpMethod,
        path: string,
        options: RequestOptions = {}
    ): Promise<T | ApiResponse<T>> {
        if (!accessToken) throw new TypeError("accessToken is required");

        const url = this.buildUrl(path, options.query);
        const headers: Record<string, string> = {
            Accept: "application/json, application/problem+json",
            Authorization: `Bearer ${accessToken}`,
            ...options.headers,
        };

        let body: BodyInit | undefined;
        if (options.body !== undefined) {
            if (isRawBody(options.body)) {
                body = options.body;
            } else {
                body = JSON.stringify(options.body);
                headers["Content-Type"] ??= "application/json";
            }
        }

        const response = await this.fetchImpl(url, { method, headers, body, signal: options.signal });
        const parsed = await parseBody(response);

        if (!response.ok) {
            const error = new ApiError(response.status, method, url, parsed, response.statusText);
            if (options.returnResponse) {
                return { ok: false, status: response.status, data: undefined, problem: error.problem };
            }
            throw error;
        }
        if (options.returnResponse) {
            return { ok: true, status: response.status, data: parsed as T, problem: null };
        }
        return parsed as T;
    }

    private buildUrl(path: string, query?: Query): string {
        const url = new URL(`${this.baseUrl}/${path.replace(/^\/+/, "")}`);
        if (query) {
            for (const [key, value] of Object.entries(query)) {
                const values = Array.isArray(value) ? value : [value];
                for (const v of values) {
                    if (v !== undefined && v !== null) url.searchParams.append(key, String(v));
                }
            }
        }
        return url.toString();
    }
}

function isRawBody(value: unknown): value is BodyInit {
    return (
        typeof value === "string" ||
        value instanceof FormData ||
        value instanceof URLSearchParams ||
        value instanceof Blob ||
        value instanceof ArrayBuffer ||
        ArrayBuffer.isView(value) ||
        value instanceof ReadableStream
    );
}

async function parseBody(response: Response): Promise<unknown> {
    if (response.status === 204 || response.status === 205) return undefined;
    const text = await response.text();
    if (!text) return undefined;
    const contentType = (response.headers.get("content-type") ?? "").toLowerCase();
    if (contentType.includes("json")) {
        try {
            return JSON.parse(text);
        } catch {
            return text;
        }
    }
    return text;
}

function toProblemDetails(body: unknown, status: number, statusText: string): ProblemDetails {
    const fields: Record<string, unknown> =
        body !== null && typeof body === "object" && !Array.isArray(body)
            ? body as Record<string, unknown>
            : {};

    // RFC 9457: ignore incorrectly typed members and default an omitted type to about:blank.
    return {
        ...fields,
        type: typeof fields.type === "string" ? fields.type : "about:blank",
        title: typeof fields.title === "string" ? fields.title : statusText || `HTTP ${status}`,
        status: typeof fields.status === "number" ? fields.status : status,
        detail: typeof fields.detail === "string" ? fields.detail : typeof body === "string" ? body : undefined,
        instance: typeof fields.instance === "string" ? fields.instance : undefined,
    };
}

export class ApiError extends Error {
  public readonly problem: ProblemDetails;

  constructor(
    public readonly status: number,
    public readonly method: string,
    public readonly url: string,
    public readonly body: unknown,
    statusText = "",
  ) {
    super(`${method} ${url} failed with ${status}`);
    this.name = "ApiError";
    this.problem = toProblemDetails(body, status, statusText);
  }

  get problemType() {
    return this.problem.type;
  }

  get isUnauthorized() {
    return this.status === 401;
  }

  get isNotFound() {
    return this.status === 404;
  }
}


/** GET /mobile/me. Billing fields are null unless canManageBilling is true. */
export type PqUser = {
    /** Only monthly Mobile accounts may view or change billing; the API enforces this too. */
    canManageBilling: boolean
    /** A stable code; any value other than Monthly is unknown and should be hidden. */
    billingType: PqBillingType | null
    serviceLevel: PqServiceLevel | null
    serviceStatus: PqServiceStatus | null
    nextBillingDate: string | null // ISO 8601 date string i.e. "2024-06-30"
    paymentInfo: PqPaymentInfo | null
}

export type PqPaymentInfo = {
    cardLast4: string | null
    cardType: string | null
    expiryMonth: number | null
    expiryYear: number | null
    cardholderName: string | null
}

export type BillingInfo = {
    billTo: BillToInfo | null
}

export type BillToInfo = {
    firstName: string | null
    lastName: string | null
    company: string | null
    address: string | null
    city: string | null
    state: string | null
    zip: string | null
    country: string | null
}

/** The closed set of billing type codes /mobile/me discloses; new codes are an announced contract change. */
export enum PqBillingType {
    Monthly = "Monthly",
}

export enum PqServiceLevel {
    AviatorMobile = "Aviator-Mobile",
    CountyMobile = "County-Mobile",
    Mobile = "Mobile"
}

export enum PqServiceStatus {
    Active = "Active",
    Inactive = "Inactive",
    Pending = "Pending"
}

export type PqUpdatePaymentMethod = {
    opaqueData: AcceptJsOpaqueData,
    billTo: BillToInfo
}
